import React, { useState } from 'react'
import { storageGet, storageSet } from '../../../../../utils/storage'
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  generateUtilityClasses,
} from '@mui/material'
import { styled } from '@mui/system'
import { visuallyHidden } from '@mui/utils'
import { getComparator, stableSort } from '../../../../../utils/sorting'
import { formatNumber } from '../../../../../utils/general'
import { useNavigate } from 'react-router-dom'
import { appRoute } from '../../../../../constants'
import { UspReportAccountsPayableAgingResult } from '../../../../../models'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'tableHead', 'tableCellName'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    margin: theme.spacing(3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },

  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
}))

interface Props {
  filterData: UspReportAccountsPayableAgingResult[]
  isLoading: boolean
}

const AccountsPayableAgingTableBlock = ({ filterData, isLoading }: Props) => {
  const navigate = useNavigate()
  const initialOrder = (storageGet('reports_accounts_payable_aging_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('reports_accounts_payable_aging_order_by') || 'StockNumber') as keyof UspReportAccountsPayableAgingResult
  const [order, setOrder] = useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = useState<keyof UspReportAccountsPayableAgingResult>(initialOrderBy)

  const handleEdit = (id: number | string) => {
    if (id) {
      navigate(appRoute.InventoryEdit.replace(':carID', String(id)))
    }
  }

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspReportAccountsPayableAgingResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('reports_accounts_payable_aging_order', isAsc ? 'desc' : 'asc')
    storageSet('reports_accounts_payable_aging_order_by', property)
  }

  const createSortHandler = (property: keyof UspReportAccountsPayableAgingResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }
  return (
    <TableContainer sx={{ maxHeight: 'calc(100vh - 118px)' }}>
      <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Vendor' ? order : false}>
              <TableSortLabel active={orderBy === 'Vendor'} direction={orderBy === 'Vendor' ? order : 'asc'} onClick={createSortHandler('Vendor')}>
                Vendor
                {orderBy === 'Vendor' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Code' ? order : false}>
              <TableSortLabel active={orderBy === 'Code'} direction={orderBy === 'Code' ? order : 'asc'} onClick={createSortHandler('Code')}>
                Code
                {orderBy === 'Code' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'User' ? order : false}>
              <TableSortLabel active={orderBy === 'User'} direction={orderBy === 'User' ? order : 'asc'} onClick={createSortHandler('User')}>
                User
                {orderBy === 'User' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Branch' ? order : false}>
              <TableSortLabel active={orderBy === 'Branch'} direction={orderBy === 'Branch' ? order : 'asc'} onClick={createSortHandler('Branch')}>
                Branch
                {orderBy === 'Branch' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'AP' ? order : false}>
              <TableSortLabel active={orderBy === 'AP'} direction={orderBy === 'AP' ? order : 'asc'} onClick={createSortHandler('AP')}>
                AP
                {orderBy === 'AP' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Account' ? order : false}>
              <TableSortLabel active={orderBy === 'Account'} direction={orderBy === 'Account' ? order : 'asc'} onClick={createSortHandler('Account')}>
                Account
                {orderBy === 'Account' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'BillNumber' ? order : false}>
              <TableSortLabel
                active={orderBy === 'BillNumber'}
                direction={orderBy === 'BillNumber' ? order : 'asc'}
                onClick={createSortHandler('BillNumber')}
              >
                Bill Number
                {orderBy === 'BillNumber' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'StockNumber' ? order : false}>
              <TableSortLabel
                active={orderBy === 'StockNumber'}
                direction={orderBy === 'StockNumber' ? order : 'asc'}
                onClick={createSortHandler('StockNumber')}
              >
                Stock #
                {orderBy === 'StockNumber' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Category1' ? order : false}>
              <TableSortLabel
                active={orderBy === 'Category1'}
                direction={orderBy === 'Category1' ? order : 'asc'}
                onClick={createSortHandler('Category1')}
              >
                1-30 Days
                {orderBy === 'Category1' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Category2' ? order : false}>
              <TableSortLabel
                active={orderBy === 'Category2'}
                direction={orderBy === 'Category2' ? order : 'asc'}
                onClick={createSortHandler('Category2')}
              >
                31-60 Days
                {orderBy === 'Category2' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Category3' ? order : false}>
              <TableSortLabel
                active={orderBy === 'Category3'}
                direction={orderBy === 'Category3' ? order : 'asc'}
                onClick={createSortHandler('Category3')}
              >
                61-90 Days
                {orderBy === 'Category3' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>

            <TableCell className={classes.tableHead} sortDirection={orderBy === 'Category4' ? order : false}>
              <TableSortLabel
                active={orderBy === 'Category4'}
                direction={orderBy === 'Category4' ? order : 'asc'}
                onClick={createSortHandler('Category4')}
              >
                91+ Days
                {orderBy === 'Category4' ? (
                  <Box component='span' sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stableSort(getComparator(order, orderBy), filterData as any[]).map((item, index) => (
            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell>{item.Vendor}</TableCell>
              <TableCell>{item.Code}</TableCell>
              <TableCell>{item.User}</TableCell>
              <TableCell>{item.Branch}</TableCell>
              <TableCell>{item.AP}</TableCell>
              <TableCell>{item.Account}</TableCell>
              <TableCell>{item.BillNumber}</TableCell>
              <TableCell className={classes.tableCellName} onClick={() => handleEdit(item.CarID as string)}>
                {item.StockNumber}
              </TableCell>
              <TableCell>{formatNumber(item.Category1 as string)}</TableCell>
              <TableCell>{formatNumber(item.Category2 as string)}</TableCell>
              <TableCell>{formatNumber(item.Category3 as string)}</TableCell>
              <TableCell>{formatNumber(item.Category4 as string)}</TableCell>
            </TableRow>
          ))}
          {filterData?.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align='center'>
                No results
              </TableCell>
            </TableRow>
          )}
          {isLoading && (
            <TableRow>
              <TableCell colSpan={10} align='center'>
                <CircularProgress />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default AccountsPayableAgingTableBlock
